/**
 * @module game/FrenzyManager
 * Controls frenzy wave timing for Classic and Zen modes.
 *
 * A frenzy wave floods the screen with fruit for a short burst.
 * The manager fires a wave signal at intervals defined in CONFIG.game:
 *  - Classic: CONFIG.game.frenzyInterval
 *  - Zen:     CONFIG.game.frenzyIntervalZen
 *
 * The slow-motion intro for frenzy entry is a rendering/feel concern; this
 * module only tracks when a wave triggers, how many fruit to spawn, and
 * whether the wave is currently active.
 */

import { CONFIG } from '../game-config.js';
import type { GameMode } from '../game-config.js';

/** Read-only view of frenzy state. */
export interface FrenzyState {
  /** Whether a frenzy wave is currently active. */
  isFrenzy: boolean;
  /** Elapsed ms since the last frenzy wave started. */
  elapsedSinceWave: number;
  /** Total frenzy waves triggered this session. */
  waveCount: number;
}

/**
 * Stateful frenzy timer — tracks time until the next wave and how many
 * fruit the wave should spawn.
 *
 * One instance per game session; call {@link FrenzyManager.reset} on restart.
 */
export class FrenzyManager {
  private readonly _mode: GameMode;
  /** Accumulated time since the last frenzy wave (ms). */
  private _sinceLastWave = 0;
  /** Whether a frenzy wave was just triggered this tick. */
  private _frenzyJustTriggered = false;
  /** Total waves triggered this session. */
  private _waveCount = 0;

  constructor(mode: GameMode) {
    this._mode = mode;
  }

  /**
   * Advances frenzy timing by `deltaMs`.
   *
   * @param deltaMs - Frame delta in milliseconds.
   * @returns A wave trigger event when a new frenzy starts, or null otherwise.
   */
  tick(deltaMs: number): FrenzyTrigger | null {
    this._sinceLastWave += deltaMs;
    this._frenzyJustTriggered = false;

    const interval =
      this._mode === 'zen'
        ? CONFIG.game.frenzyIntervalZen
        : CONFIG.game.frenzyInterval;

    if (this._sinceLastWave >= interval) {
      this._sinceLastWave -= interval;
      this._frenzyJustTriggered = true;
      this._waveCount += 1;

      const [min, max] = CONFIG.game.frenzyFruitCount;
      const count = min + Math.floor(Math.random() * (max - min + 1));

      return { fruitCount: count, waveIndex: this._waveCount };
    }

    return null;
  }

  /** Time (ms) since the last frenzy wave was triggered. */
  get elapsedSinceWave(): number {
    return this._sinceLastWave;
  }

  /** How many waves have fired this session. */
  get waveCount(): number {
    return this._waveCount;
  }

  /** Current frenzy state snapshot for HUD / renderer. */
  get state(): FrenzyState {
    return {
      isFrenzy: this._frenzyJustTriggered,
      elapsedSinceWave: this._sinceLastWave,
      waveCount: this._waveCount,
    };
  }

  /** Resets all state for a new game session. */
  reset(): void {
    this._sinceLastWave = 0;
    this._frenzyJustTriggered = false;
    this._waveCount = 0;
  }
}

/** Payload emitted when a frenzy wave is triggered. */
export interface FrenzyTrigger {
  /** Number of fruit to spawn in this wave (random in [min, max] from CONFIG). */
  fruitCount: number;
  /** Ordinal index of this wave (1-based). */
  waveIndex: number;
}
